import { useState, useCallback, memo, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const PhotoAlbumConfigForm = memo(function PhotoAlbumConfigForm({
  config,
  onUpdate,
  widgetId,
  spaceId,
}) {
  const [localConfig, setLocalConfig] = useState({
    title: config.title || "",
    showTitle: config.showTitle !== false, // Default to true
  });

  // Sync local state when config changes from outside
  useEffect(() => {
    setLocalConfig({
      title: config.title || "",
      showTitle: config.showTitle !== false,
    });
  }, [config.title, config.showTitle]);

  // Add handler to stop propagation
  const stopPropagation = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      e.nativeEvent?.stopImmediatePropagation();
    }
  }, []);

  const handleTitleChange = useCallback(
    (e) => {
      const newTitle = e.target.value;
      setLocalConfig((prev) => ({ ...prev, title: newTitle }));
      onUpdate({ title: newTitle });
    },
    [onUpdate]
  );

  const handleShowTitleToggle = useCallback(
    (checked) => {
      setLocalConfig((prev) => ({ ...prev, showTitle: checked }));
      onUpdate({ showTitle: checked });
    },
    [onUpdate]
  );

  return (
    <div
      className="space-y-4"
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
    >
      {/* Album Title */}
      <div>
        <Label htmlFor="albumTitle" className="text-white">
          Album Title
        </Label>
        <Input
          id="albumTitle"
          value={localConfig.title}
          onChange={handleTitleChange}
          placeholder="My Photo Album"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/50"
        />
        <p className="mt-1 text-xs text-white/50">
          The title displayed at the top of your photo album
        </p>
      </div>

      {/* Settings */}
      <div className="space-y-3">
        {/* Show Title Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <Label htmlFor="showTitle" className="text-white">
              Show Title
            </Label>
            <p className="text-xs text-white/50">
              Display the album title above the photos
            </p>
          </div>
          <Switch
            id="showTitle"
            checked={localConfig.showTitle}
            onCheckedChange={handleShowTitleToggle}
          />
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-6 p-3 bg-white/5 rounded-lg border border-white/10">
        <p className="text-xs text-white/70">
          <strong>Tip:</strong> Photos will be arranged in a polaroid-style
          layout with slight rotations and overlapping. Drag and drop multiple
          images or use the add button to build your album.
        </p>
      </div>
    </div>
  );
});

PhotoAlbumConfigForm.displayName = "PhotoAlbumConfigForm";

export default PhotoAlbumConfigForm;
